import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Stack,
    IconButton,
    Button,
    Tooltip,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Link
} from '@mui/material';
import { Search, Eye, ExternalLink, RefreshCw } from 'lucide-react';
import { getBecomeHosts } from '../../Services/BecomeHosts/BecomeHostsApi';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import GlobalPagination from '../../Components/Common/Globalpagination';

const statusConfig = {
    all: { label: 'All Applications', color: '#64748B', activeBg: '#F8FAFC', activeBorder: '#64748B' },
    pending: { label: 'Pending', color: '#B78103', activeBg: '#FFF8E1', activeBorder: '#B78103' },
    under_review: { label: 'Under Review', color: '#7B1FA2', activeBg: '#F3E5F5', activeBorder: '#7B1FA2' },
    contacted: { label: 'Contacted', color: '#D97706', activeBg: '#FFF8E7', activeBorder: '#FFBD65' },
    need_changes: { label: 'Need Changes', color: '#C2410C', activeBg: '#FFEDD5', activeBorder: '#C2410C' },
    approved: { label: 'Approved', color: '#2E7D32', activeBg: '#E8F5E9', activeBorder: '#2E7D32' },
    rejected: { label: 'Rejected', color: '#C62828', activeBg: '#FFEBEE', activeBorder: '#C62828' }
};

const BecomeHostApplications = () => {
    const navigate = useNavigate();

    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [statusCounts, setStatusCounts] = useState({
        all: 0,
        pending: 0,
        under_review: 0,
        contacted: 0,
        need_changes: 0,
        approved: 0,
        rejected: 0
    });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [limit, setLimit] = useState(10);

    // Fetch Host Applications List
    const fetchList = useCallback(async (querySearch = '', queryStatus = '', currentPage = 1, currentLimit = limit) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getBecomeHosts(currentPage, currentLimit, querySearch, queryStatus);

            if (response && response.success) {
                setApplications(response.data || []);
                if (response.statusCounts) {
                    setStatusCounts(response.statusCounts);
                }
                const pag = response.pagination;
                if (pag) {
                    setTotalItems(pag.totalItems || 0);
                    setTotalPages(pag.totalPages || 1);
                } else {
                    setTotalItems(response.totalItems || response.total || (response.data ? response.data.length : 0));
                    setTotalPages(response.totalPages || 1);
                }
            } else {
                setApplications([]);
                setError("Failed to retrieve applications.");
            }
        } catch (err) {
            console.error("Error fetching become host list:", err);
            setError(err.response?.data?.message || "Failed to fetch applications. Please try again.");
            setApplications([]);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    // Debounce search function
    useEffect(() => {
        const handler = setTimeout(() => {
            fetchList(searchTerm, statusFilter, 1, limit);
            setPage(1);
        }, 400);

        return () => clearTimeout(handler);
    }, [searchTerm, statusFilter, limit, fetchList]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchList(searchTerm, statusFilter, newPage, limit);
    };

    const handleStatusCardClick = (statusKey) => {
        const newStatus = statusKey === 'all' ? '' : statusKey;
        setStatusFilter(newStatus);
        setPage(1);
        fetchList(searchTerm, newStatus, 1, limit);
    };

    // Navigate to View Details Page
    const handleViewDetails = (item) => {
        navigate(`/host-applications/view-details/${item.id}`);
    };

    const renderStatusChip = (statusKey) => {
        const config = statusConfig[statusKey?.toLowerCase()] || { color: '#666', activeBg: '#F5F5F5', label: statusKey || 'Pending' };
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    bgcolor: config.activeBg,
                    color: config.color,
                    fontWeight: 600,
                    fontSize: '0.725rem',
                    height: '24px',
                    borderRadius: '4px',
                    textTransform: 'capitalize'
                }}
            />
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 3,
                        gap: 2
                    }}
                >
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Become Host Applications
                    </Typography>

                    {/* Filter & Search Bar */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        width: { xs: '100%', sm: 'auto' }
                    }}>
                        <TextField
                            placeholder="Search by org, contact, phone, email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{
                                flex: { xs: '1 1 200px', sm: '0 0 260px' },
                                bgcolor: 'white',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 1 }}>
                                        <Search size={18} color="#666" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl size="small" sx={{ flex: { xs: '1 1 140px', sm: '0 0 auto' }, minWidth: 140, bgcolor: 'white' }}>
                            <InputLabel sx={{ fontSize: '0.85rem' }}>Status Filter</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status Filter"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value);
                                    setPage(1);
                                }}
                                sx={{
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                                }}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="under_review">Under Review</MenuItem>
                                <MenuItem value="contacted">Contacted</MenuItem>
                                <MenuItem value="need_changes">Need Changes</MenuItem>
                                <MenuItem value="approved">Approved</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>

                        <IconButton
                            onClick={() => fetchList(searchTerm, statusFilter, page)}
                            sx={{
                                bgcolor: 'white',
                                border: '1px solid #E0E0E0',
                                borderRadius: '6px',
                                p: '8px',
                            }}
                        >
                            <RefreshCw size={18} color="#555" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Status Summary Cards (Side by Side - modeled after Bookings page) */}
                <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                    {[
                        { key: 'all', label: 'All', count: statusCounts.all || totalItems || 0 },
                        { key: 'pending', label: 'Pending', count: statusCounts.pending || 0 },
                        { key: 'under_review', label: 'Under Review', count: statusCounts.under_review || 0 },
                        { key: 'contacted', label: 'Contacted', count: statusCounts.contacted || 0 },
                        { key: 'need_changes', label: 'Need Changes', count: statusCounts.need_changes || 0 },
                        { key: 'approved', label: 'Approved', count: statusCounts.approved || 0 },
                        { key: 'rejected', label: 'Rejected', count: statusCounts.rejected || 0 }
                    ].map((card) => {
                        const cfg = statusConfig[card.key];
                        const isActive = (card.key === 'all' && !statusFilter) || statusFilter === card.key;
                        return (
                            <Paper
                                key={card.key}
                                elevation={0}
                                onClick={() => handleStatusCardClick(card.key)}
                                sx={{
                                    flex: '1 1 120px',
                                    minWidth: '120px',
                                    p: 1.5,
                                    borderRadius: '6px',
                                    bgcolor: isActive ? cfg.activeBg : '#FFFFFF',
                                    border: isActive ? `1.5px solid ${cfg.activeBorder}` : '1px solid #E2E8F0',
                                    cursor: 'pointer',
                                    boxShadow: 'none'
                                }}
                            >
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {cfg.label}
                                </Typography>
                                <Typography sx={{ fontSize: '1.4rem', fontWeight: 600, color: '#0F172A', mt: 0.5 }}>
                                    {card.count}
                                </Typography>
                            </Paper>
                        );
                    })}
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Applications Table (Basic Values Table Format) */}
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '8px', border: '1px solid #EAEAEA', mb: 3 }}>
                    <Table sx={{ width: '100%' }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                {[
                                    { label: 'S.No', align: 'left' },
                                    { label: 'Organization', align: 'left' },
                                    { label: 'Contact Person', align: 'left' },
                                    { label: 'Phone & Email', align: 'left' },
                                    { label: 'Location', align: 'left' },
                                    { label: 'Business Type', align: 'left' },
                                    { label: 'Offers', align: 'left' },
                                    { label: 'Status', align: 'left' },
                                    { label: 'Created At', align: 'left' },
                                    { label: 'Action', align: 'right' },
                                ].map((h) => (
                                    <TableCell
                                        key={h.label}
                                        align={h.align}
                                        sx={{
                                            fontWeight: 600,
                                            color: '#475569',
                                            fontSize: '0.7rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.6px',
                                            py: 1.5,
                                            px: 1.5,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {h.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : applications.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                        No host applications found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                applications.map((item, idx) => (
                                    <TableRow key={item.id} sx={{
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td, &:last-child th': { border: 0 }
                                    }}>
                                        <TableCell align="left" sx={{ fontSize: '0.75rem', color: '#64748B', py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            {((page - 1) * limit) + idx + 1}
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5 }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: 200 }}>
                                                <Tooltip title={item.organizationName || 'N/A'} arrow placement="top">
                                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#0F172A', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {item.organizationName || 'N/A'}
                                                    </Typography>
                                                </Tooltip>
                                                {item.website && (
                                                    <Link href={item.website.startsWith('http') ? item.website : `https://${item.website}`} target="_blank" rel="noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                                                        <ExternalLink size={12} color="#D97706" />
                                                    </Link>
                                                )}
                                            </Box>
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>
                                                {item.contactPersonName || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', whiteSpace: 'nowrap' }}>
                                                {item.countryCode ? `${item.countryCode} ` : ''}{item.phone || ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.gmailId || item.email || ''}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>
                                                {item.location || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#475569' }}>
                                                {item.businessType || 'N/A'}{item.establishedYear ? ` (${item.establishedYear})` : ''}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: '4px', maxWidth: 180 }}>
                                                {Array.isArray(item.whatDoYouOffer) && item.whatDoYouOffer.length > 0 ? (
                                                    item.whatDoYouOffer.map((offer, oIdx) => (
                                                        <Chip
                                                            key={oIdx}
                                                            label={offer}
                                                            size="small"
                                                            sx={{ fontSize: '0.68rem', height: '20px', borderRadius: '4px', bgcolor: '#F1F5F9', color: '#334155' }}
                                                        />
                                                    ))
                                                ) : (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>N/A</Typography>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            {renderStatusChip(item.status)}
                                        </TableCell>

                                        <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(item.createdAt)}
                                        </TableCell>

                                        <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Eye size={14} />}
                                                onClick={() => handleViewDetails(item)}
                                                sx={{
                                                    borderColor: '#CBD5E1',
                                                    color: '#334155',
                                                    textTransform: 'none',
                                                    fontSize: '0.72rem',
                                                    height: '28px',
                                                    py: 0,
                                                    px: 1,
                                                    borderRadius: '4px',
                                                    '&:hover': {
                                                        borderColor: '#94A3B8',
                                                        bgcolor: '#F8FAFC'
                                                    }
                                                }}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination Section */}
                <GlobalPagination
                    page={page}
                    pageSize={limit}
                    totalRecords={totalItems}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(newLimit) => {
                        setLimit(newLimit);
                        setPage(1);
                        fetchList(searchTerm, statusFilter, 1, newLimit);
                    }}
                    loading={loading}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default BecomeHostApplications;
